import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'wouter';
import { ArrowRight, Ticket, UserRoundCog, type LucideIcon } from 'lucide-react';
import { platformService, type PlatformDashboard } from '@/services/api/platform.service';
import { Badge } from '@/components/ui/badge';
import { PlatformLayout } from '@/components/platform/PlatformLayout';

interface ToolCardProps {
  href: string;
  icon: LucideIcon;
  title: string;
  description: string;
  stats: { label: string; value: number | undefined }[];
  badge?: ReactNode;
}

function ToolCard({ href, icon: Icon, title, description, stats, badge }: ToolCardProps) {
  return (
    <Link
      href={href}
      className="group flex flex-col rounded-2xl border border-line bg-surface p-6 shadow-card transition hover:border-brand-500 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
          <Icon className="h-6 w-6" aria-hidden />
        </span>
        {badge}
      </div>

      <h2 className="font-display text-lg font-semibold text-ink">{title}</h2>
      <p className="mt-1 text-sm text-ink-subtle">{description}</p>

      <dl className="mt-5 grid grid-cols-2 gap-3">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-xl bg-surface-2 px-3 py-2">
            <dt className="text-xs text-ink-subtle">{stat.label}</dt>
            <dd className="font-display text-xl font-semibold text-ink">{stat.value ?? '—'}</dd>
          </div>
        ))}
      </dl>

      <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-brand-700">
        Open
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
      </span>
    </Link>
  );
}

/** Super-admin landing page: one card per console tool, with headline numbers. */
export default function PlatformDashboardPage() {
  const [, setLocation] = useLocation();
  const [stats, setStats] = useState<PlatformDashboard | null>(null);

  useEffect(() => {
    platformService.getDashboard().then(setStats).catch((err) => {
      // No/invalid platform session → back to platform login.
      if (err?.response?.status === 401) setLocation('/platform/login');
    });
  }, [setLocation]);

  const invalidCodes = stats?.invalidInvitationCodesCount ?? 0;

  return (
    <PlatformLayout title="Dashboard" subtitle="What do you want to do?">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <ToolCard
          href="/platform/users"
          icon={UserRoundCog}
          title="Impersonate a user"
          description="Find any user across all organizations and see the app exactly as they do."
          stats={[
            { label: 'Users', value: stats?.usersCount },
            { label: 'Organizations', value: stats?.organizationsCount },
          ]}
          badge={stats && stats.activeImpersonationsCount > 0
            ? <Badge tone="warn">{stats.activeImpersonationsCount} open session{stats.activeImpersonationsCount > 1 ? 's' : ''}</Badge>
            : undefined}
        />
        <ToolCard
          href="/platform/invitation-codes"
          icon={Ticket}
          title="Invitation codes"
          description="Create the codes for the invite-only landing pages, check them against Stripe and see who signed up."
          stats={[
            { label: 'Active codes', value: stats?.activeInvitationCodesCount },
            { label: 'Invited signups', value: stats?.invitedSignupsCount },
          ]}
          badge={stats && stats.invitationCodesCount > 0 && (invalidCodes > 0
            ? <Badge tone="danger">{invalidCodes} need{invalidCodes > 1 ? '' : 's'} attention</Badge>
            : <Badge tone="success">All codes valid</Badge>)}
        />
      </div>
    </PlatformLayout>
  );
}
